/** The shape a behaviour has been making, in its own hue. */
/** A week has to move by at least this share of its own level to fill the lane. */
const MIN_HALF_SPAN_OF_LEVEL = 0.125;

function median(xs: ReadonlyArray<number>): number {
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function BehaviourSpark({
  values,
  accent,
  gathering,
  className,
}: {
  /** One value per day, oldest first. Null is a day with no reading. */
  values: ReadonlyArray<number | null>;
  accent: string | undefined;
  gathering: boolean;
  className?: string;
}) {
  const base = `snap-spark${className ? ` ${className}` : ""}`;
  if (gathering || values.filter((v) => v !== null).length < 2) {
    return (
      <svg
        className={`${base} is-empty`}
        viewBox="0 0 100 28"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line x1={0} y1={22} x2={100} y2={22} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }
  const reported = values.filter((v): v is number => v !== null);
  const lo = Math.min(...reported);
  const hi = Math.max(...reported);
  /** Scaled against a floor, not against the week's own extremes. */
  const middle = median(reported);
  // A week of straight zeroes leaves every term zero, and an unfloored half
  // divides the lane by nothing.
  const half = Math.max((hi - lo) / 2, Math.abs(middle) * MIN_HALF_SPAN_OF_LEVEL) || 1;
  const step = 100 / (values.length - 1);
  // Centred on the week's midpoint, so a flat week sits down the middle of the
  // lane instead of along the bottom of it.
  const mid = (hi + lo) / 2;
  const y = (v: number) => clamp(15 - ((v - mid) / (half * 2)) * 22, 3, 27);
  // A day nobody reported breaks the line rather than pulling it to the floor:
  // drawing it as a zero put a plunge in a week the member did not miss.
  const runs: string[] = [];
  // A day sitting alone between two gaps has no run to join, and dropping it
  // left the only reading of that week invisible. It is drawn as a point.
  const islands: Array<{ x: number; y: number }> = [];
  let run: Array<{ x: number; y: number }> = [];
  const flush = () => {
    if (run.length > 1) runs.push(run.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" "));
    else if (run.length === 1 && run[0]) islands.push(run[0]);
    run = [];
  };
  values.forEach((v, i) => {
    if (v === null) {
      flush();
      return;
    }
    run.push({ x: i * step, y: y(v) });
  });
  flush();
  const lastIndex = values.reduce<number>((held, v, i) => (v === null ? held : i), -1);
  const last = values[lastIndex] ?? lo;
  return (
    <svg
      className={base}
      viewBox="0 0 100 28"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ color: accent }}
    >
      {/* The lane is a week wide however little of it reported, so hovering a
          short line says how short and out of what. */}
      <title>{`Last ${values.length} days, oldest first. ${reported.length} reported.`}</title>
      {/* The lane the days would have sat on, so a week with gaps reads as days
          that did not report rather than as a chart that failed to draw. */}
      {reported.length < values.length ? (
        <line
          className="snap-spark-lane"
          x1={0}
          y1={22}
          x2={100}
          y2={22}
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      {runs.map((points) => (
        <polyline
          key={points}
          points={points}
          fill="none"
          strokeWidth={1.75}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {islands.map((p) => (
        <circle key={p.x} cx={p.x} cy={p.y} r={1.75} vectorEffect="non-scaling-stroke" />
      ))}
      <circle cx={lastIndex * step} cy={y(last)} r={2.5} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
